import { cn } from "@/lib/utils";
import { formatDateShort } from "@/lib/format";
import { PRIORITY_LABELS, priorityTone } from "@/lib/follow-up-calc";
import { TONE_BG } from "@/lib/widget-status";
import type { FollowUpWithRelations } from "@/hooks/use-follow-ups";

interface FollowUpSideListProps {
  open: FollowUpWithRelations[];
  done: FollowUpWithRelations[];
  currentId: string | null;
  onSelect: (id: string) => void;
}

function PriorityDot({ followUp }: { followUp: FollowUpWithRelations }) {
  const tone = priorityTone(followUp.priority);
  return (
    <span
      role="img"
      aria-label={PRIORITY_LABELS[followUp.priority]}
      className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", tone ? TONE_BG[tone] : "bg-muted-foreground/40")}
    />
  );
}

/** Kompakt liste over uka. Klikk hopper til kortet; fullførte står gjennomstreket nederst. */
export default function FollowUpSideList({ open, done, currentId, onSelect }: FollowUpSideListProps) {
  const count = open.length + done.length;
  return (
    <section aria-label="Oppfølginger denne uken" className="border-[1.5px] border-foreground bg-card">
      <h2 className="border-b border-foreground/20 px-4 py-3 font-display text-base font-semibold">
        Oppfølginger denne uken ({count})
      </h2>
      {count === 0 ? (
        <p className="p-4 text-sm text-muted-foreground">Ingen oppfølginger denne uken.</p>
      ) : (
        <ul className="divide-y divide-border/60">
          {open.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => onSelect(f.id)}
                aria-current={f.id === currentId ? "true" : undefined}
                className={cn("flex w-full items-start gap-3 px-4 py-2.5 text-left hover:bg-muted/50", f.id === currentId && "bg-muted")}
              >
                <PriorityDot followUp={f} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{f.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {f.customer?.name ?? "Ukjent kunde"} · {formatDateShort(f.due_date)}
                  </span>
                </span>
              </button>
            </li>
          ))}
          {done.map((f) => (
            <li key={f.id} className="flex items-start gap-3 px-4 py-2.5 text-muted-foreground line-through">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-muted-foreground/30" aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm">{f.title}</span>
                <span className="block truncate text-xs">{f.customer?.name ?? "Ukjent kunde"}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
